import { blue, green, purple, red, yellow, orange } from '@mui/material/colors';
import gaiaTheme from '../../root-components/theme';

const huePalettes = [blue, green, purple, red, yellow, orange];

export function hexToRgb(hex: string) {
  const clean = hex.replace('#', '');
  const val = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const int = parseInt(val, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

/** Normalises a CSS colour to lowercase 6-digit hex (#rrggbb) for display. Supports #rgb, #rrggbb, and rgb()/rgba(). */
export function colorToHex(input: string | undefined | null): string {
  if (input == null || input === '') return '';
  const s = String(input).trim();
  if (s === 'transparent') return '';
  if (s.startsWith('#')) {
    const raw = s.slice(1);
    if (raw.length === 3 && /^[0-9a-fA-F]{3}$/.test(raw)) {
      return `#${raw
        .split('')
        .map((c) => c + c)
        .join('')
        .toLowerCase()}`;
    }
    if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.toLowerCase()}`;
    return s;
  }
  const rgb = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb) {
    const r = Math.min(255, Math.max(0, Number(rgb[1])));
    const g = Math.min(255, Math.max(0, Number(rgb[2])));
    const b = Math.min(255, Math.max(0, Number(rgb[3])));
    return `#${[r, g, b].map((x) => Math.round(x).toString(16).padStart(2, '0')).join('')}`;
  }
  return s;
}

export function rgbToHsl(r: number, g: number, b: number) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0; let s = 0; const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h *= 60;
  }
  return { h, s, l };
}

export function getHsl(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHsl(r, g, b);
}

export function pickShadeByLightness(hex: string): 300 | 500 | 700 {
  const { l } = getHsl(hex);
  if (l >= 0.7) return 300;
  if (l <= 0.35) return 700;
  return 500;
}

export function getMixedColorPalette(primaryColor: string) {
  const shade = pickShadeByLightness(primaryColor);
  const toned = huePalettes.map((hue) => hue[shade]);
  const primaryHue = getHsl(primaryColor).h;

  // Buckets correspond to [blue, green, purple, red, yellow, orange]
  const getBucketIndex = (h: number): number => {
    if (h >= 190 && h <= 250) return 0;        // blue
    if (h >= 75 && h <= 165) return 1;         // green
    if (h >= 260 && h <= 330) return 2;        // purple
    if (h >= 0 && h <= 30) return 3;           // red
    if (h >= 45 && h <= 75) return 4;          // yellow
    if (h >= 15 && h <= 45) return 5;          // orange
    return 0;                                  // blue (default)
  };
  const excludedIndex = getBucketIndex(primaryHue);
  const companions = toned
    .filter((_, idx) => idx !== excludedIndex)
    .filter((c) => c && c.toLowerCase() !== primaryColor.toLowerCase())
    .slice(0, 5);
  return [primaryColor, ...companions];
}

function hslToHex(h: number, s: number, l: number): string {
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const secondary = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const match = l - chroma / 2;
  const sector = Math.floor(((h % 360) + 360) % 360 / 60);
  const [r, g, b] = [
    [chroma, secondary, 0], [secondary, chroma, 0], [0, chroma, secondary],
    [0, secondary, chroma], [secondary, 0, chroma], [chroma, 0, secondary],
  ][sector];
  return `#${[r, g, b]
    .map((channel) => Math.round((channel + match) * 255).toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * Ordered light-to-dark ramp in the brand hue, for encoding a QUANTITY (map bands, heat scales).
 *
 * `getMixedColorPalette` is categorical — its colours are deliberately unrelated so distinct
 * series stay distinguishable. Using it for an ordered quantity would imply differences that
 * are not there. This keeps hue and saturation fixed and varies only lightness, so the ramp
 * reads as "more" in one direction and still separates in greyscale.
 */
export function getSequentialColorPalette(primaryColor: string, steps = 5): string[] {
  const { h, s } = getHsl(primaryColor);
  // Stops short of pure white/black at either end: a near-white circle disappears against the
  // basemap and a near-black one loses its hue.
  const LIGHTEST = 0.82;
  const DARKEST = 0.22;
  const saturation = Math.max(0.25, Math.min(0.85, s));
  if (steps <= 1) return [hslToHex(h, saturation, DARKEST)];
  return Array.from({ length: steps }, (_, index) =>
    hslToHex(h, saturation, LIGHTEST - ((LIGHTEST - DARKEST) * index) / (steps - 1)));
}

export const MixedColorPalatte = getMixedColorPalette(gaiaTheme.palette.primary.main);


